import React, { useState, useEffect } from 'react';
import styles from './jiraSettings.module.css';
import { Form, Row, Col, Switch, message, AutoComplete, Button } from 'antd';
import { get } from '../../utils/fetch';
import { ConnectPrompt } from './ConnectPrompt';
import { useIntegrationConnection } from './useIntegrationConnection';

type OptionType = { value: string; label: string };

interface Props {
  projectId: string;
  organizationId: string;
  integration:
  | {
    enabled: boolean;
    id: string;
    settings: {
      project: OptionType;
      new_tasks: boolean;
      new_comments: boolean;
    };
  }
  | undefined;
  onDisconnect: () => void;
}

const GithubSettings: React.FC<Props> = (props: Props) => {
  const [form] = Form.useForm();
  const { integration, organizationId, projectId, onDisconnect } = props;

  const {
    isEnabled,
    getConnectedState,
    saveSettings,
    disconnect
  } = useIntegrationConnection({
    organizationId,
    projectId,
    integration,
    integrationCode: 'Github',
    onDisconnect
  });

  const [selectedProject, setSelectedProject] = useState(
    integration?.settings?.project
  );

  const [autoPost, setAutoPost] = useState(
    integration?.settings?.new_tasks ?? false
  );
  const [newComments, setNewComments] = useState(
    integration?.settings?.new_comments ?? false
  );

  const [remoteProjectOptions, setRemoteProjectOptions] = useState<OptionType[]>(
    []
  );
  const [isProjectsLoading, setIsProjectsLoading] = useState(false);

  const getRepositories = async () => {
    if (!integration?.enabled) return;
    setIsProjectsLoading(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/remote_projects?id=${integration.id}`
      );
      setRemoteProjectOptions(results);
    } catch (error) {
      message.error('Failed to fetch GitHub repositories. Please try again.');
    } finally {
      setIsProjectsLoading(false);
    }
  };

  useEffect(() => {
    if (integration?.enabled) {
      getConnectedState();
      getRepositories();
    }
  }, [integration, projectId, organizationId]);

  const renderIntegrationSettings = () => {
    return (
      <div>
        <Form
          form={form}
          layout="horizontal"
          onFinish={() =>
            saveSettings(
              {
                project: selectedProject,
                new_tasks: autoPost,
                new_comments: newComments
              },
              { form }
            )
          }
          autoComplete="off"
          requiredMark={false}
        >
          <div className={styles.jiraRequired}>
            <div className={styles.requiredBubble}>REQUIRED</div>
            <h3>Connect your GitHub repository</h3>
            <Row className={styles.jiraEditRow}>
              <Col span={24}>
                <Form.Item
                  name="project"
                  label={
                    <span className={styles.requiredLabel}>
                      <span>*</span>GitHub Repository
                    </span>
                  }
                  className={styles.jiraProject}
                  rules={[
                    {
                      required: true,
                      message: 'Please select a GitHub repository.'
                    }
                  ]}
                  initialValue={selectedProject?.value}
                >
                  <AutoComplete
                    className={styles.jiraSelect}
                    placeholder="Type to search for a repository"
                    options={remoteProjectOptions}
                    onSelect={(_value, option) => {
                      setSelectedProject(option as OptionType);
                    }}
                    onChange={value => {
                      if (!value) setSelectedProject(undefined);
                    }}
                    filterOption={(inputValue, option) =>
                      option!.label.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                    }
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <div className={styles.jiraWorkflows}>
            <h3>Choose your desired workflow</h3>
            <Row className={styles.jiraEditRow}>
              <Form.Item
                label="Automatically post new tasks to GitHub"
                className={styles.autoPost}
              >
                <Switch checked={autoPost} onChange={e => setAutoPost(e)} />
              </Form.Item>
            </Row>
            <Row className={styles.jiraEditRow}>
              <Form.Item
                className={styles.newComments}
                label="Automatically send new BugHerd comments to GitHub"
              >
                <Switch
                  checked={newComments}
                  onChange={e => setNewComments(e)}
                />
              </Form.Item>
            </Row>
          </div>

          <Row className={styles.jiraEditRow} justify="space-between" align="middle">
            <Col>
              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  shape="round"
                  htmlType="submit"
                  className={styles.save}
                >
                  Save
                </Button>
              </Form.Item>
            </Col>
            <Col>
              <Button
                type="text"
                shape="round"
                onClick={() => disconnect({ deleteFallback: true })}
                className={styles.disconnectButton}
                danger
              >
                Disconnect
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      {isEnabled ? (
        renderIntegrationSettings()
      ) : (
        <ConnectPrompt
          integrationName="github"
          projectId={projectId}
          organizationId={organizationId}
          type="Github"
          display="GitHub"
          provider="github"
        />
      )}
    </div>
  );
};

export default GithubSettings;
